// landing.component.ts
import { Component, OnDestroy, OnInit } from '@angular/core';
import { HeroBannerComponent } from '../../components/hero-banner/hero-banner.component';
import { HeaderComponent } from '../../components/header/header.component';
import { PhotographerListComponent } from '../../components/landing/photographer-list/photographer-list.component';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { SeoService } from '../../../core/services/seo/seo.service';
import { AnalyticsService } from '../../../core/services/analytics/analytics.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import {Footer} from '../../../core/components/footer/footer';

@Component({
  selector: 'app-landing',
  imports: [HeroBannerComponent, HeaderComponent, PhotographerListComponent, Footer],
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.scss'
})
export class LandingComponent implements OnInit, OnDestroy {
  public city: string | null = null;
  public tags: string[] | null = null;

  private filters$ = new Subject<{ city: string | null; tags: string[] | null }>();
  private subs = new Subscription();
  private firstViewSent = false;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly seoService: SeoService,
    private readonly analytics: AnalyticsService,
  ) {
    this.route.queryParams.subscribe((params: Params) => {
      const newCity = params['city'] || null;
      const newTags = params['tags']
        ? (Array.isArray(params['tags']) ? params['tags'] : String(params['tags']).split(','))
        : null;

      const changed =
        newCity !== this.city ||
        JSON.stringify(newTags || []) !== JSON.stringify(this.tags || []);

      this.city = newCity;
      this.tags = newTags;

      if (changed) this.filters$.next({ city: this.city, tags: this.tags });
    });
  }

  public ngOnInit() {
    this.seoService.setupSEOForMarketplace();

    // Fire a single search_view when first meaningful state is known
    const s1 = this.filters$
      .pipe(
        map(v => JSON.stringify(v)),
        distinctUntilChanged()
      )
      .subscribe(() => {
        if (!this.firstViewSent) {
          this.analytics.event('search_view', {
            city: this.city || 'all',
            tags_count: this.tags?.length || 0,
            tags_list: (this.tags || []).slice(0, 10),
          });
          this.firstViewSent = true;
        }
      });

    // Debounced changes to filters
    const s2 = this.filters$
      .pipe(
        debounceTime(300),
        map(v => JSON.stringify(v)),
        distinctUntilChanged()
      )
      .subscribe(() => {
        this.analytics.event('search_filters_change', {
          city: this.city || 'all',
          tags_count: this.tags?.length || 0,
          tags_list: (this.tags || []).slice(0, 10),
        });
      });

    this.subs.add(s1);
    this.subs.add(s2);
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public cityChanged(city: string | null): void {
    if (this.city !== city) {
      this.city = city;
      this.router.navigate([], { queryParams: { city }, queryParamsHandling: 'merge', replaceUrl: true });
      this.filters$.next({ city: this.city, tags: this.tags });
    }
  }

  public tagsChanged(tags: string[]): void {
    if (this.tags !== tags) {
      this.tags = tags;
      this.router.navigate([], { queryParams: { tags }, queryParamsHandling: 'merge', replaceUrl: true });
      this.filters$.next({ city: this.city, tags: this.tags });
    }
  }

  // Optional hooks fired by child list (see below)
  public onResultsImpression(payload: { resultsCount: number; topIds?: number[] }) {
    this.analytics.event('search_results_impression', {
      city: this.city || 'all',
      tags_count: this.tags?.length || 0,
      results_count: payload.resultsCount,
      top_5_ids: payload.topIds?.slice(0, 5),
    });
  }

  public onResultClick(payload: { photographerId: number; rank: number }) {
    this.analytics.event('search_result_click', {
      city: this.city || 'all',
      tags_count: this.tags?.length || 0,
      photographer_id: payload.photographerId,
      rank: payload.rank,
    });
  }
}
